import { motion } from "framer-motion";
import PackageGrid from "@/components/PackageGrid";
import CountdownOffer from "@/components/CountdownOffer";
import { MaskLine, Reveal } from "@/components/Reveal";
import { BadgeCheck, ChevronDown, Clock, DollarSign, Star, Zap } from "lucide-react";

const TRUST_POINTS = [
  { icon: DollarSign, label: "Best Price, Guaranteed" },
  { icon: BadgeCheck, label: "7-Year Waterproofing Warranty" },
  { icon: Clock, label: "3-Week Completion Guaranteed" },
  { icon: Zap, label: "Fully Licensed, In-House Trades" },
];

const HomePage = () => (
  <main data-testid="home-page">
    <section
      data-testid="hero-section"
      className="relative overflow-hidden px-5 pb-16 pt-28 md:px-10 md:pb-20 md:pt-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-sdh-sky/10 blur-3xl"
      />
      <div className="relative mx-auto max-w-4xl text-center">
        <MaskLine delay={0.05}>
          <div
            data-testid="google-reviews-hero"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2"
          >
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-white/80">
              <span className="text-lg font-bold leading-none text-[#FFC107]">★★★★★</span>
              600+ Google reviews
            </span>
            <span className="h-1 w-1 rounded-full bg-sdh-sky" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-sdh-sky">
              Bathroom Renovations — Sydney, NSW
            </span>
          </div>
        </MaskLine>

        <Reveal delay={0.1}>
          <p
            data-testid="dont-pay-kicker"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-500/15 border border-red-500/30 px-4 py-1.5 font-display text-base uppercase tracking-wider text-red-300 md:text-lg"
          >
            <span className="line-through decoration-2">$30K+</span>
            <span className="text-white/60">→</span>
            <span>Don't pay it</span>
          </p>
        </Reveal>

        <h1 className="mt-6 font-display text-6xl leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl">
          <MaskLine delay={0.2}>Your bathroom</MaskLine>
          <MaskLine delay={0.35}>
            <span className="text-sdh-sky">doesn't need</span>
          </MaskLine>
          <MaskLine delay={0.5}>
            to cost a house deposit.
          </MaskLine>
        </h1>

        <div className="mt-7 flex justify-center">
          <CountdownOffer />
        </div>

        <MaskLine delay={0.65}>
          <p className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-white/70 md:text-lg">
            No hidden extras, no surprises, no "we'll get back to you with a quote". Just two
            fixed prices and a bathroom you'll actually want to show off.
          </p>
        </MaskLine>

        <Reveal delay={0.7}>
          <div className="mt-8 flex justify-center">
            <a
              href="#packages"
              data-testid="hero-quote-button"
              className="cta-pulse-sky group inline-flex items-center gap-3 rounded-full bg-sdh-sky px-9 py-4 font-display text-xl uppercase tracking-wider text-[#0B1D39] transition-colors duration-300 hover:bg-white md:text-2xl"
            >
              Get My Fixed Quote
              <ChevronDown size={20} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.75}>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5">
            {TRUST_POINTS.map((t) => (
              <span
                key={t.label}
                data-testid={`trust-point-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white/85"
              >
                <t.icon size={16} className="text-sdh-sky" />
                {t.label}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.8}>
          <div
            data-testid="waiting-hero"
            className="mx-auto mt-10 max-w-2xl rounded-3xl border border-sdh-sky/30 bg-sdh-sky/[0.08] p-8 text-center md:p-10"
          >
            <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-white md:text-5xl">
              What are you waiting for?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/70 md:text-lg">
              Either you're a competitor drooling over our prices and wondering how we do it, or
              you're a smart customer who doesn't want to pay a house deposit for a bathroom.
            </p>
            <p className="mt-3 text-sm italic text-white/55 md:text-base">
              (If it's the first one — we'll never tell. If it's the second — let's talk.)
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.85}>
          <div className="mt-2" />
        </Reveal>
      </div>

      <div className="relative mx-auto mt-12 max-w-5xl md:mt-16">
        <Reveal delay={0.2}>
          <div
            data-testid="pick-package-cue"
            className="mb-8 flex items-center justify-center gap-3"
          >
            <span className="font-display text-2xl uppercase tracking-wider text-sdh-sky md:text-3xl">
              Pick your package
            </span>
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            >
              <ChevronDown size={26} className="text-sdh-sky" />
            </motion.span>
          </div>
        </Reveal>

        <PackageGrid />

        <Reveal delay={0.3}>
          <div
            data-testid="value-comparison"
            className="mt-8 rounded-2xl border border-sdh-sky/25 bg-sdh-sky/[0.06] p-6 text-center md:p-8"
          >
            <p className="font-display text-2xl uppercase leading-tight text-white md:text-3xl">
              Others charge $30K+. We charge $17,990.
            </p>
            <p className="mt-2 text-sm text-white/60 md:text-base">
              Same licensed trades, same quality finish, same 7-year warranty — just without
              the inflated markup.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.35}>
          <p
            data-testid="hero-reassurance-line"
            className="mt-6 text-center text-sm text-white/50"
          >
            Fixed price + GST · Any bathroom size · Licensed & fully insured · HBCF cover ·
            7-year warranty · 600+ 5-star reviews
          </p>
        </Reveal>
      </div>
    </section>
  </main>
);

export default HomePage;
